import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import Panel from '@prisjakt/ui-panel';
import Pagination from '@prisjakt/ui-pagination';
import Button from '@prisjakt/ui-button';
import Rating from '@prisjakt/ui-rating';
import UserReviewRow from '@prisjakt/ui-user-review-row';
import UserAvatar from '../../components/user-avatar';

import AdContainer from '../../components/ads';
import ReportReviewModal from './components/report';
import UserReviewPrompt from './components/dialog-prompt';
import UserReviewDialog from './components/dialog';
import InfoPageLink from '../../components/info-page-link';

import * as S from './styles';

class UserReviews extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { reportReview: null };
    this.renderReview = this.renderReview.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  scrollToTop() {
    if (this.containerElement && 'scrollIntoView' in this.containerElement) {
      this.containerElement.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }

  createGroups() {
    const { nodes } = this.props;
    const newReviewLimit = new Date().setFullYear(new Date().getFullYear() - 1);

    const newReviews = nodes.filter(
      item => new Date(item.createdAt).getTime() >= newReviewLimit
    );
    const oldReviews = nodes.filter(
      item => new Date(item.createdAt).getTime() < newReviewLimit
    );

    return [newReviews, oldReviews];
  }

  renderSummary(nodes) {
    const { t } = this.context;
    const { summary, user, belongsTo } = this.props;

    return (
      <S.Summary>
        {summary.rating && (
          <Rating
            rate={summary.rating}
            count={summary.count}
            counterPosition="bottom"
            countTranslation={{
              domain: 'reviews',
              key: 'rating.based-on-n-reviews.md',
            }}
            showRate
            showRateAs="text"
            showCounter={belongsTo.type === 'product'}
          />
        )}

        <S.Prompt highlighted={this.props.nodes.length === 0}>
          <UserReviewPrompt
            firstToReview={nodes.length === 0 && !summary.rating}
            firstToReviewText={t('reviews', 'info.first-review', {
              name: belongsTo.name,
            })}
            requireLogin={!user}
            control={
              <Button color="primary" data-test="WriteReviewButton">
                {t('reviews', 'button.write-review')}
              </Button>
            }
          />
        </S.Prompt>
      </S.Summary>
    );
  }

  renderReview(review) {
    const { t } = this.context;
    const { user, belongsTo } = this.props;

    return !review.underInvestigation ? (
      <UserReviewRow
        id={review.id}
        key={review.id}
        rating={review.rating}
        date={review.localizedCreatedAt}
        purchaseDate={
          this.isPurchaseDateModuleActive() && review.purchaseDate
            ? review.purchaseDate
            : undefined
        }
        editDate={
          review.localizedCreatedAt !== review.localizedUpdatedAt
            ? review.localizedUpdatedAt
            : ''
        }
        displayName={review.user && review.user.displayName}
        userReviewCount={review.user && review.user.reviewCount}
        text={review.reviewText}
        translation={review.translation}
        editControl={
          user && review.user && user.id === review.user.id ? (
            <UserReviewPrompt
              control={
                <S.EditButton>{t('reviews', 'button.edit')}</S.EditButton>
              }
            />
          ) : null
        }
        avatar={
          <UserAvatar
            styling="dark"
            size={30}
            displayName={review.user && review.user.displayName}
            avatar={review.user && review.user.avatar}
          />
        }
        actions={[
          {
            name: t('feedback', 'report-review.report-button'),
            handler: reportReview => this.setState({ reportReview }),
          },
        ]}
        reply={
          review.reply && belongsTo.name
            ? {
                name: belongsTo.name,
                text: review.reply,
                date: review.replyDate,
              }
            : undefined
        }
      />
    ) : null;
  }

  isPurchaseDateModuleActive() {
    return (
      this.context.market.modules.userReviewPurchaseDate.enabled &&
      this.props.belongsTo.type === 'product'
    );
  }

  render() {
    const { t } = this.context;
    const {
      user,
      nodes,
      myReview,
      pageInfo,
      belongsTo,
      groupByOldAndNew,
    } = this.props;

    const [reviews, oldReviews] = groupByOldAndNew
      ? this.createGroups()
      : [nodes, []];

    return (
      <div
        ref={node => {
          this.containerElement = node;
        }}
      >
        <ReportReviewModal
          review={this.state.reportReview}
          onComplete={() => this.setState({ reportReview: null })}
        />
        {user && (
          <UserReviewDialog
            belongsToId={belongsTo.id}
            reviewType={belongsTo.type.toUpperCase()}
            myReview={myReview}
            setDisplayName={!user.displayName}
            title={t('reviews', 'dialog.title', { name: belongsTo.name })}
            setPurchaseDate={this.isPurchaseDateModuleActive()}
          />
        )}
        <Panel
          padding="both"
          footer={
            <Pagination
              pageInfo={pageInfo}
              onNext={this.scrollToTop}
              onPrev={this.scrollToTop}
              onReset={this.scrollToTop}
            />
          }
        >
          {this.renderSummary(nodes)}

          <AdContainer
            placement="panorama_extra"
            min={861}
            max={10000}
            scalable
            scaleBase=".pj-ui-panel--row"
            spacing={{ top: '10px', bottom: '10px' }}
          />

          <AdContainer
            placement="dubbelpanorama1"
            min={0}
            max={860}
            spacing={{ top: '10px', bottom: '10px' }}
          />

          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {this.props.nodes.length > 0 && (
              <>
                <S.InfoLink>
                  <InfoPageLink
                    id={this.context.market.infoPageMap.aboutReviews}
                  >
                    {t('reviews', 'reviews.about-reviews')}
                  </InfoPageLink>
                </S.InfoLink>

                <S.SortedBy style={{ marginLeft: 'auto' }}>
                  <T domain="reviews">info.sort-by-date</T>
                </S.SortedBy>
              </>
            )}
          </div>

          {reviews.map(this.renderReview)}

          {oldReviews.length > 0 && (
            <S.OldReviewsHeader>
              <S.SortedBy>
                <T domain="reviews">info.sort-by-date</T>
              </S.SortedBy>
              <h5>
                <T domain="reviews">panel.old-title</T>
              </h5>
              <p>
                <T domain="reviews">panel.old-lead</T>
              </p>

              {oldReviews.map(this.renderReview)}
            </S.OldReviewsHeader>
          )}
        </Panel>
      </div>
    );
  }
}

const { shape, number, string, bool, oneOf, arrayOf, oneOfType } = PropTypes;

const review = shape({
  id: string,
  rating: number,
  reviewText: string,
  createdAt: string,
  localizedCreatedAt: string,
  localizedUpdatedAt: string,
  purchaseDate: string,
  underInvestigation: bool,
  user: shape({
    id: oneOfType([number, string]),
    name: string,
    displayName: string,
    reviewCount: number,
    avatar: string,
  }),
  translations: shape({
    text: string,
    language: string,
    service: shape({
      name: string,
      uri: string,
    }),
  }),
});

UserReviews.propTypes = {
  belongsTo: shape({
    id: number,
    name: string,
    type: oneOf(['product', 'store']),
  }).isRequired,

  summary: shape({
    rating: number,
    count: number,
  }),

  myReview: review,

  user: shape({
    id: oneOfType([number, string]),
    displayName: string,
  }),

  nodes: arrayOf(review),
  pageInfo: shape({
    hasNext: bool,
    hasPrevious: bool,
    limit: number,
    offset: number,
    sort: shape({
      name: string,
      direction: oneOf(['asc', 'desc']),
      available: arrayOf(string),
    }),
  }),
  groupByOldAndNew: bool,
};

UserReviews.defaultProps = {
  myReview: undefined,
  user: undefined,
  summary: undefined,
  nodes: undefined,
  pageInfo: undefined,
  groupByOldAndNew: false,
};

UserReviews.contextType = AppContext;

export default connect(state => {
  if (!state.areas.account.me) {
    return { user: null };
  }

  return {
    user: {
      id: state.areas.account.me.id,
      displayName: state.areas.account.me.displayName,
    },
  };
})(UserReviews);
